import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useAuth } from "react-oidc-context";
import { useSearchParams } from 'react-router-dom';
import { Database, Globe, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { FilterBar } from './common/FilterBar';
import { SectionHeader } from './common/SectionHeader';
import { DCUsTable } from './partner/DCUsTable';
import { CountriesTable } from './partner/CountriesTable';
import { PoliciesFilter } from './partner/PoliciesFilter';
import { PoliciesTable } from './partner/PoliciesTable';
import { PolicyStatusModal } from './partner/PolicyStatusModal';
import { PPIMAPI } from '../../services/api/ppimAPI';
import { generateSnapshotOptionsFrom } from '../../utils/date_utils';
import { dcuFilters, countryFilters } from '../../utils/constant';
import { setSnapshot, setPartnerSummary, setProfileDcu, setProfileCountries, setProfilePolicies, updatePolicyDecision } from "../../store/appSlice";

const DEFAULT_FILTERS = {
  dcuCountry: 'all',
  connectionStatus: 'all',
  dcus: 'all',
  connectionChanges: 'all',
  ppus: 'all',
  policies: 'all',
  country: 'all',
};

export const PartnerProfile = () => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const { snapshot, partnerSummary, partnerSummarySnapshot, profileDcu, profileCountries, profilePolicies } = useSelector((state) => state.app);

  const token = auth.user?.access_token;
  const api = useMemo(() => PPIMAPI(token), [token]);

  const snapshotOptions = generateSnapshotOptionsFrom(11, 2025).map(opt => ({
    ...opt,
    value: opt.value // Make sure this matches YYYY-MM-01 format if needed
  }));

  const [searchParams] = useSearchParams();
  const [selectedPartner, setSelectedPartner] = useState(searchParams.get('partner') || 'All Partners');
  const [partnerProfileTab, setPartnerProfileTab] = useState(searchParams.get('tab') || 'dcus');

  const initialFilters = searchParams.has('new') || searchParams.has('partner')
    ? {
        ...DEFAULT_FILTERS,
        dcus: searchParams.get('new') === 'dcus' ? 'new' : 'all',
        connectionChanges: searchParams.get('new') === 'connection_change' ? 'changed' : 'all',
        ppus: searchParams.get('new') === 'ppus' ? 'new' : 'all',
      }
    : JSON.parse(localStorage.getItem('filterValues')) ?? DEFAULT_FILTERS;

  // Derive partner names from Redux state
  const partnersList = useMemo(
    () => partnerSummary.map(p => p.partner).sort(),
    [partnerSummary]
  );

  // Fetch partner summary into Redux if not already loaded for this snapshot
  useEffect(() => {
    if (!token || !snapshot) return;
    if (partnerSummarySnapshot === snapshot && partnerSummary.length > 0) return;
    api.getPartnerData(snapshot)
      .then(data => {
        if (Array.isArray(data)) {
          dispatch(setPartnerSummary({ data, snapshot }));
        }
      })
      .catch(err => {
        console.error('Error loading partners list:', err);
      });
  }, [token, snapshot]);

  // Fetch partner summary into Redux if not already loaded for this snapshot
  useEffect(() => {
    if (!token || !snapshot) return;
    if (partnerSummarySnapshot === snapshot && partnerSummary.length > 0) return;
    api.getPartnerData(snapshot)
      .then(data => {
        if (Array.isArray(data)) {
          dispatch(setPartnerSummary({ data, snapshot }));
        }
      })
      .catch(err => {
        console.error('Error loading partners list:', err);
      });
  }, [token, snapshot]);

  const [filterValues, setFilterValues] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [policiesFilters, setPoliciesFilters] = useState({
    decision: [],
    status: [],
    monthAssessed: 'all',
    policies: searchParams.get('new') === 'policies' ? 'new' : 'all',
  });
  const [selectedPolicyRow, setSelectedPolicyRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(1000);

  const loadProfileData = async (filters = filterValues, currentPage = 1, currentPageSize = pageSize, pFilters = policiesFilters) => {
    if (!token) return;

    localStorage.setItem('filterValues', JSON.stringify(filters));

    let cacheKey;
    if (partnerProfileTab === 'dcus') {
      cacheKey = `dcus|${snapshot}|${selectedPartner}|${JSON.stringify(filters)}|p${currentPage}|s${currentPageSize}`;
    } else if (partnerProfileTab === 'countries') {
      cacheKey = `countries|${snapshot}|${selectedPartner}|${JSON.stringify(filters)}`;
    } else {
      cacheKey = `policies|${snapshot}|${selectedPartner}|${JSON.stringify(pFilters)}|p${currentPage}|s${currentPageSize}`;
    }

    // Hit Redux cache
    if (partnerProfileTab === 'dcus' && profileDcu.cacheKey === cacheKey) return;
    if (partnerProfileTab === 'countries' && profileCountries.cacheKey === cacheKey) return;
    if (partnerProfileTab === 'policies' && profilePolicies.cacheKey === cacheKey) return;

    setPage(currentPage);
    setLoading(true);
    try {
      if (partnerProfileTab === 'dcus') {
        const response = await api.getDCUs(snapshot, selectedPartner, filters, currentPage, currentPageSize);
        dispatch(setProfileDcu({ cacheKey, data: response.data || [], total: response.total ?? 0, totalPages: response.total_pages ?? 1 }));
      } else if (partnerProfileTab === 'countries') {
        const data = await api.getCountries(snapshot, selectedPartner, filters);
        dispatch(setProfileCountries({ cacheKey, data }));
      } else if (partnerProfileTab === 'policies') {
        const response = await api.getPolicies(snapshot, selectedPartner, pFilters, currentPage, currentPageSize);
        dispatch(setProfilePolicies({ cacheKey, data: response.data || [], total: response.total ?? 0, totalPages: response.total_pages ?? 1 }));
      }
    } catch (err) {
      console.error('Error loading partner profile:', err);
      toast.error('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadProfileData(filterValues, 1);
  }, [snapshot, partnerProfileTab, filterValues, selectedPartner]);

  const handleFilterChange = (name, value) => {
    const updatedFilters = { ...filterValues, [name]: value };
    setFilterValues(updatedFilters);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadProfileData(filterValues, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
    loadProfileData(filterValues, 1, newSize);
  };

  const handleSort = (field) => {
    console.log('Sort by:', field);
  };

  const handleSnapshotChange = (value) => {
    dispatch(setSnapshot(value)); // update Redux state
    localStorage.setItem('savedSnapshot', value);
  };

  return (
    <>
      {loading && (
        <div className="fixed inset-0 bg-white/70 flex items-center justify-center z-50">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      <div className="flex items-start justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            Partner Profile: {selectedPartner}
          </h2>
          <p className="text-sm text-gray-600">Detailed compliance and performance metrics</p>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Partner:</label>
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedPartner}
              onChange={(e) => setSelectedPartner(e.target.value)}
            >
              <option value="All Partners">All Partners</option>
              {partnersList.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Snapshot:</label>
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={snapshot}
              onChange={(e) => handleSnapshotChange(e.target.value)}
            >
              {snapshotOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex gap-8 px-6">
            {['dcus', 'countries', 'policies'].map(tab => (
              <button
                key={tab}
                className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  partnerProfileTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setPartnerProfileTab(tab)}
              >
                {tab === 'dcus' && <Database className="w-4 h-4" />}
                {tab === 'countries' && <Globe className="w-4 h-4" />}
                {tab === 'policies' && <FileText className="w-4 h-4" />}
                {tab === 'dcus' ? 'DCUs' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {partnerProfileTab === 'dcus' && (
            <>
              <FilterBar filters={dcuFilters} values={filterValues} onChange={handleFilterChange} />
              <SectionHeader title="DCUs" description={`Data Collection URLs (${profileDcu.total.toLocaleString()} total)`} />
              <DCUsTable data={profileDcu.data} onSort={handleSort} />
              {profileDcu.data.length > 0 && (
                <div className="flex items-center justify-between px-2 py-3 border-t border-gray-200 mt-2">
                  <span className="text-sm text-gray-600">
                    Showing {profileDcu.data.length} of {profileDcu.total.toLocaleString()} records
                  </span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <label className="text-sm text-gray-600">Rows per page:</label>
                      <select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        {[50, 100, 250, 500, 1000].map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">Page {page} of {profileDcu.totalPages}</span>
                    <button
                      disabled={page === profileDcu.totalPages}
                      onClick={() => handlePageChange(page + 1)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {partnerProfileTab === 'countries' && (
            <>
              <FilterBar filters={countryFilters} values={filterValues} onChange={handleFilterChange} />
              <CountriesTable data={profileCountries.data} onSort={handleSort} />
            </>
          )}

          {partnerProfileTab === 'policies' && (
            <>
              <PoliciesFilter
                filters={policiesFilters}
                onApply={(f) => {
                  const merged = { ...f, policies: policiesFilters.policies };
                  setPoliciesFilters(merged);
                  setPage(1);
                  loadProfileData(filterValues, 1, pageSize, merged);
                }}
              />
              <PoliciesTable
                data={profilePolicies.data}
                total={profilePolicies.total}
                onDecisionClick={(row) => setSelectedPolicyRow(row)}
              />
              {profilePolicies.data.length > 0 && (
                <div className="flex items-center justify-between px-2 py-3 border-t border-gray-200 mt-2">
                  <span className="text-sm text-gray-600">
                    Showing {profilePolicies.data.length} of {profilePolicies.total.toLocaleString()} records
                  </span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <label className="text-sm text-gray-600">Rows per page:</label>
                      <select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        {[50, 100, 250, 500, 1000].map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">Page {page} of {profilePolicies.totalPages}</span>
                    <button
                      disabled={page === profilePolicies.totalPages}
                      onClick={() => handlePageChange(page + 1)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selectedPolicyRow && (
        <PolicyStatusModal
          policy={selectedPolicyRow}
          onClose={() => setSelectedPolicyRow(null)}
          onFetchHistory={(ppfId) => api.getDecisionHistory(ppfId)}
          onUpdate={async (_id, changes) => {
            const username = auth.user?.profile?.name ?? auth.user?.profile?.email ?? null;
            const result = await api.updatePolicyStatus(selectedPolicyRow.ppf_id, { ...changes, requested_by: username, approved_by: username });
            dispatch(updatePolicyDecision({ ppf_id: selectedPolicyRow.ppf_id, decision: result.decision, decision_changed: result.decision_changed }));
            setSelectedPolicyRow(null);
          }}
        />
      )}
    </>
  );
};